/**
 * GRC Copilot Service API
 * React Query hooks for the AI Copilot microservice
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post, uploadFile } from '@/lib/api/client'
import { keycloak, getTenantId } from '@/lib/auth/keycloak'
import { resolveApiUrl } from '@/lib/api/config'

// ── Types ────────────────────────────────────────────────────────────────────

export interface CopilotMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
  feedback?: 'positive' | 'negative'
}

export interface CopilotChatRequest {
  message: string
  moduleContext: string
  pageContext: string
  conversationHistory?: CopilotMessage[]
  entityData?: Record<string, unknown>
  page_data?: Record<string, unknown>
}

export interface CopilotChatResponse {
  id: string
  content: string
  suggestions?: string[]
  sources?: { title: string; url?: string }[]
}

export interface CopilotSummaryRequest {
  context: string
  data: unknown
}

export interface CopilotSummaryResponse {
  keyInsight: string
  trend: string
  recommendedAction: string
  generatedAt: string
}

export interface CopilotSearchResult {
  id: string
  title: string
  category: 'risk' | 'control' | 'policy' | 'assessment' | 'compliance' | 'evidence' | 'framework' | 'other'
  description: string
  relevance: number
  module: string
  url?: string
}

export interface CopilotSearchResponse {
  results: CopilotSearchResult[]
  query: string
  totalResults: number
}

export interface DocumentAnalysisResult {
  keyClauses: { clause: string; section: string; importance: 'high' | 'medium' | 'low' }[]
  mappedControls: { controlId: string; controlName: string; mapping: string }[]
  identifiedObligations: { obligation: string; dueDate?: string; responsible?: string }[]
  summary: string
  documentType: string
}

export interface WhatIfScenario {
  description: string
  affectedParameters: Record<string, { current: number; proposed: number }>
}

export interface WhatIfResult {
  scenarioId: string
  description: string
  impacts: {
    metric: string
    before: number
    after: number
    delta: number
    severity: 'critical' | 'high' | 'medium' | 'low'
  }[]
  overallRisk: 'increased' | 'decreased' | 'stable'
  recommendations: string[]
}

export interface ActionRecommendation {
  id: string
  title: string
  description: string
  module: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  impactEstimate: string
  dueDate: string
  effort: 'low' | 'medium' | 'high'
}

export interface AnomalyDetection {
  id: string
  severity: 'critical' | 'high' | 'medium' | 'low'
  description: string
  affectedMetric: string
  currentValue: number
  expectedValue: number
  deviation: number
  suggestedInvestigation: string
  detectedAt: string
}

// ── RAG semantic-search / similar / analyze (knowledge-service) ───────────────

export interface KnowledgeEntity {
  entity_type: string
  entity_id: string
  title: string
  snippet: string
  score: number
  url?: string
  metadata?: Record<string, unknown>
}

export interface KnowledgeCitation {
  entity_type?: string
  entity_id?: string
  title?: string
  snippet?: string
  url?: string
  score?: number
}

export interface KnowledgeAnalyzeResult {
  answer: string
  citations: KnowledgeCitation[]
  grounded: boolean
}

export interface KnowledgeSearchRequest {
  query: string
  types?: string[]
  limit?: number
}

export interface KnowledgeAnalyzeRequest {
  question: string
  types?: string[]
  entity_type?: string
  entity_id?: string
}

// ── Advanced copilot: form-fill / file-extract / advise (ai-orchestrator) ─────

export interface CopilotFormFillRequest {
  form_schema: unknown
  prompt?: string
  document_text?: string
  module?: string
}

export interface CopilotFormFillResult {
  fields: Record<string, unknown>
  confidence?: number
  notes?: string
  unfilled?: string[]
}

export interface CopilotExtractFileResult {
  document_text: string
  fields?: Record<string, unknown>
  summary?: string
}

export interface CopilotAdviceRequest {
  module?: string
  entity_type?: string
  entity_id?: string
  question?: string
}

export interface CopilotAdviceResult {
  advice: string
  grounded: boolean
  citations: KnowledgeCitation[]
}

// ── API Methods ──────────────────────────────────────────────────────────────

export const copilotApi = {
  chat: async (request: CopilotChatRequest): Promise<CopilotChatResponse> => {
    return post<CopilotChatResponse>('/ai/copilot/chat', request)
  },

  chatStream: async (
    message: string,
    context: { moduleContext: string; pageContext: string; entityData?: Record<string, unknown>; page_data?: Record<string, unknown> },
    conversationHistory: CopilotMessage[],
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
  ) => {
    const response = await fetch(resolveApiUrl('/ai/copilot/chat/stream'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {}),
        'X-Tenant-ID': getTenantId() || '',
      },
      body: JSON.stringify({
        message,
        moduleContext: context.moduleContext,
        pageContext: context.pageContext,
        entityData: context.entityData || null,
        page_data: context.page_data || null,
        conversationHistory: conversationHistory.map((m) => ({ role: m.role, content: m.content })),
      }),
    })

    if (!response.ok) throw new Error('Stream failed')

    const reader = response.body?.getReader()
    const decoder = new TextDecoder()

    if (!reader) throw new Error('No reader')

    let buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6))
            if (data.type === 'token') onToken(data.content)
            else if (data.type === 'done') onDone()
            else if (data.type === 'error') onError(data.content)
          } catch {
            // Ignore malformed SSE lines
          }
        }
      }
    }
  },

  summarize: async (request: CopilotSummaryRequest): Promise<CopilotSummaryResponse> => {
    return post<CopilotSummaryResponse>('/ai/copilot/summarize', request)
  },

  search: async (query: string): Promise<CopilotSearchResponse> => {
    return get<CopilotSearchResponse>('/ai/copilot/search', { params: { q: query } })
  },

  analyzeDocument: async (
    file: File,
    moduleContext: string,
    onProgress?: (progress: number) => void
  ): Promise<DocumentAnalysisResult> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('moduleContext', moduleContext)

    return post<DocumentAnalysisResult>('/ai/copilot/analyze-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent: { loaded: number; total?: number }) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
  },

  whatIf: async (
    scenario: WhatIfScenario,
    moduleContext: string,
    currentData: unknown
  ): Promise<WhatIfResult> => {
    return post<WhatIfResult>('/ai/copilot/what-if', {
      scenario,
      moduleContext,
      currentData,
    })
  },

  getRecommendations: async (
    moduleContext: string,
    tenantData?: unknown
  ): Promise<ActionRecommendation[]> => {
    const d = await get<any>('/ai/copilot/recommendations', {
      params: { moduleContext, ...(tenantData ? { tenantData: JSON.stringify(tenantData) } : {}) },
    })
    // Backend now returns a rich object { recommendations, items, ... }; older
    // shape was a bare array. Normalize so ActionRecommender always gets an array.
    if (Array.isArray(d)) return d
    return (d?.recommendations ?? d?.items ?? []) as ActionRecommendation[]
  },

  getAnomalies: async (
    moduleContext: string,
    metrics?: unknown
  ): Promise<AnomalyDetection[]> => {
    const d = await get<any>('/ai/copilot/anomalies', {
      params: { moduleContext, ...(metrics ? { metrics: JSON.stringify(metrics) } : {}) },
    })
    if (Array.isArray(d)) return d
    return (d?.anomalies ?? d?.items ?? []) as AnomalyDetection[]
  },

  submitFeedback: async (
    messageId: string,
    rating: 'positive' | 'negative',
    context: { moduleContext: string; pageContext: string },
    comment?: string,
  ) => {
    return post('/ai/copilot/feedback', {
      message_id: messageId,
      rating,
      comment,
      module_context: context.moduleContext,
      page_context: context.pageContext,
    })
  },

  // ── RAG semantic-search (knowledge-service) ────────────────────────────────

  /** Cross-entity semantic search. Returns ranked results across all types. */
  searchEntities: async (
    query: string,
    types?: string[],
    limit = 12,
  ): Promise<KnowledgeEntity[]> => {
    const body: KnowledgeSearchRequest = { query, ...(types ? { types } : {}), limit }
    const d = await post<any, KnowledgeSearchRequest>('/knowledge/entities/search', body)
    if (Array.isArray(d)) return d as KnowledgeEntity[]
    // knowledge-service SearchResponse returns the array under `hits`; keep
    // results/items as fallbacks for any gateway-normalized shape.
    return (d?.hits ?? d?.results ?? d?.items ?? []) as KnowledgeEntity[]
  },

  /** Vector-similar entities for a given record ("more like this"). */
  similarEntities: async (
    entityType: string,
    entityId: string,
    limit = 8,
  ): Promise<KnowledgeEntity[]> => {
    const d = await get<any>('/knowledge/entities/similar', {
      params: { entity_type: entityType, entity_id: entityId, limit },
    })
    if (Array.isArray(d)) return d as KnowledgeEntity[]
    // knowledge-service SearchResponse returns the array under `hits`; keep
    // results/items as fallbacks for any gateway-normalized shape.
    return (d?.hits ?? d?.results ?? d?.items ?? []) as KnowledgeEntity[]
  },

  /** Grounded RAG question-answering over the tenant's knowledge base. */
  analyzeEntities: async (
    request: KnowledgeAnalyzeRequest,
  ): Promise<KnowledgeAnalyzeResult> => {
    const d = await post<any, KnowledgeAnalyzeRequest>('/knowledge/entities/analyze', request)
    return {
      answer: d?.answer ?? '',
      citations: (d?.citations ?? []) as KnowledgeCitation[],
      grounded: Boolean(d?.grounded),
    }
  },

  // ── Advanced copilot form-fill (ai-orchestrator) ───────────────────────────

  /** Populate a form from a natural-language prompt and/or extracted document text. */
  fillForm: async (request: CopilotFormFillRequest): Promise<CopilotFormFillResult> => {
    const d = await post<any, CopilotFormFillRequest>('/ai/copilot/fill-form', request)
    return {
      fields: (d?.fields ?? {}) as Record<string, unknown>,
      confidence: d?.confidence,
      notes: d?.notes,
      unfilled: (d?.unfilled ?? []) as string[],
    }
  },

  /** Upload a file → OCR/extract text and (optionally) auto-fill a form schema. */
  extractFile: async (
    file: File,
    formSchema?: unknown,
    module?: string,
  ): Promise<CopilotExtractFileResult> => {
    const formData = new FormData()
    formData.append('file', file)
    if (formSchema !== undefined) formData.append('form_schema', JSON.stringify(formSchema))
    if (module) formData.append('module', module)
    const d = await post<any>('/ai/copilot/extract-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return {
      document_text: d?.document_text ?? '',
      fields: (d?.fields ?? undefined) as Record<string, unknown> | undefined,
      summary: d?.summary,
    }
  },

  /** Contextual, grounded advice for a module/entity. */
  advise: async (request: CopilotAdviceRequest): Promise<CopilotAdviceResult> => {
    const d = await post<any, CopilotAdviceRequest>('/ai/copilot/advise', request)
    return {
      advice: d?.advice ?? '',
      grounded: Boolean(d?.grounded),
      citations: (d?.citations ?? []) as KnowledgeCitation[],
    }
  },
}

// ── Copilot v2 — suggest-links / generate-record / inbox / reg-impact ──────────
//
// Advanced generative/agentic endpoints on the SAME /ai/copilot router. These
// call LLMs (OSS default, Claude on board-grade) so they use GENEROUS timeouts
// well above the 30s axios default. Array responses are normalized across the
// backend's differing keys (hits / suggestions / items / results).

/** LLM-heavy generation (generate-record, reg-impact, suggest-links w/ rationale). */
const COPILOT_LLM_TIMEOUT = 120000 // 2 min
/** Inbox aggregates narrative context + a bounded advise() call server-side. */
const COPILOT_INBOX_TIMEOUT = 90000 // 1.5 min

/** Pull the array out of whatever key the copilot-v2 backend used. */
function normalizeArray<T>(d: any): T[] {
  if (Array.isArray(d)) return d as T[]
  return (d?.hits ?? d?.suggestions ?? d?.items ?? d?.results ?? []) as T[]
}

export interface CopilotSuggestion {
  entity_type: string
  entity_id: string
  title: string
  score: number
  rationale?: string
  url?: string
}

export interface SuggestLinksRequest {
  entity_type: string
  entity_id: string
  target_type: string
  module?: string
  limit?: number
  min_score?: number
  with_rationale?: boolean
  source_title?: string
  language?: string
}

export interface SuggestLinksResult {
  source: { entity_type: string; entity_id: string; title: string }
  suggestions: CopilotSuggestion[]
  model: string
}

export interface GenerateRecordSchemaField {
  name: string
  label?: string
  type?: string
  required?: boolean
  options?: string[]
}

export interface GenerateRecordRequest {
  entity_type: string
  module: string
  prompt: string
  form_schema?: GenerateRecordSchemaField[]
  language?: string
  board_grade?: boolean
  ground_with_rag?: boolean
}

export interface GenerateRecordResult {
  fields: Record<string, unknown>
  body_markdown: string
  sections: { heading: string; content: string }[]
  confidence: number
  citations: KnowledgeCitation[]
  grounded: boolean
  unfilled: string[]
  notes: string
  model: string
  credits_used: number
}

export interface CopilotInboxItem {
  id: string
  type: string
  title: string
  detail: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  module: string
  grounded: boolean
  cta?: { label: string; action: { op: string; to?: string } }
}

export interface CopilotInboxResult {
  items: CopilotInboxItem[]
  generated_at: string
  model: string
  credits_used: number
}

export interface RegImpactImpactedItem {
  entity_id: string
  title: string
  score: number
  impact: 'high' | 'medium' | 'low'
  why: string
}

export interface RegImpactActionItem {
  title: string
  priority: string
  entity_ref: string
  effort: string
}

export interface RegImpactRequest {
  regulation_text: string
  regulation_ref?: string
  scope_types?: string[]
  language?: string
  board_grade?: boolean
  limit_per_type?: number
}

export interface RegImpactResult {
  summary: string
  impacted: Record<string, RegImpactImpactedItem[]>
  action_plan: RegImpactActionItem[]
  citations: KnowledgeCitation[]
  grounded: boolean
  model: string
  credits_used: number
}

Object.assign(copilotApi, {
  /** F3 — suggest existing entities of `target_type` to link to a source record. */
  suggestLinks: async (req: SuggestLinksRequest): Promise<SuggestLinksResult> => {
    const d = await post<any, SuggestLinksRequest>(
      '/ai/copilot/suggest-links',
      { with_rationale: true, limit: 8, language: 'fr', ...req },
      { timeout: COPILOT_LLM_TIMEOUT },
    )
    return {
      source: (d?.source ?? {
        entity_type: req.entity_type,
        entity_id: req.entity_id,
        title: req.source_title ?? '',
      }) as SuggestLinksResult['source'],
      suggestions: normalizeArray<CopilotSuggestion>(d),
      model: d?.model ?? '',
    }
  },

  /** F1 — generate a full, ready-to-submit record draft (fields + long-form body). */
  generateRecord: async (req: GenerateRecordRequest): Promise<GenerateRecordResult> => {
    const d = await post<any, GenerateRecordRequest>(
      '/ai/copilot/generate-record',
      { language: 'fr', ground_with_rag: true, form_schema: [], ...req },
      { timeout: COPILOT_LLM_TIMEOUT },
    )
    return {
      fields: (d?.fields ?? {}) as Record<string, unknown>,
      body_markdown: d?.body_markdown ?? '',
      sections: (d?.sections ?? []) as { heading: string; content: string }[],
      confidence: typeof d?.confidence === 'number' ? d.confidence : 0,
      citations: (d?.citations ?? []) as KnowledgeCitation[],
      grounded: Boolean(d?.grounded),
      unfilled: (d?.unfilled ?? []) as string[],
      notes: d?.notes ?? '',
      model: d?.model ?? '',
      credits_used: d?.credits_used ?? 0,
    }
  },

  /** F9 — proactive next-best-action inbox (aggregated real tenant signals). */
  getInbox: async (module = 'all', limit = 10): Promise<CopilotInboxResult> => {
    const d = await get<any>('/ai/copilot/inbox', {
      params: { module, limit },
      timeout: COPILOT_INBOX_TIMEOUT,
    })
    return {
      items: normalizeArray<CopilotInboxItem>(d),
      generated_at: d?.generated_at ?? '',
      model: d?.model ?? '',
      credits_used: d?.credits_used ?? 0,
    }
  },

  /** F4 — regulatory-change impact analysis + action plan. */
  regImpact: async (req: RegImpactRequest): Promise<RegImpactResult> => {
    const d = await post<any, RegImpactRequest>(
      '/ai/copilot/reg-impact',
      {
        language: 'fr',
        scope_types: ['policy', 'control', 'risk', 'obligation'],
        limit_per_type: 10,
        ...req,
      },
      { timeout: COPILOT_LLM_TIMEOUT },
    )
    return {
      summary: d?.summary ?? '',
      impacted: (d?.impacted ?? {}) as Record<string, RegImpactImpactedItem[]>,
      action_plan: (d?.action_plan ?? []) as RegImpactActionItem[],
      citations: (d?.citations ?? []) as KnowledgeCitation[],
      grounded: Boolean(d?.grounded),
      model: d?.model ?? '',
      credits_used: d?.credits_used ?? 0,
    }
  },
})

// Augment the copilotApi type so the new methods are visible to TS callers
// (they are attached via Object.assign above, which TS can't infer on its own).
export interface CopilotApiV2 {
  suggestLinks: (req: SuggestLinksRequest) => Promise<SuggestLinksResult>
  generateRecord: (req: GenerateRecordRequest) => Promise<GenerateRecordResult>
  getInbox: (module?: string, limit?: number) => Promise<CopilotInboxResult>
  regImpact: (req: RegImpactRequest) => Promise<RegImpactResult>
}
export const copilotApiV2 = copilotApi as typeof copilotApi & CopilotApiV2

// ── F8 — Agentic tool-planner (POST /ai/copilot/agent/plan) ────────────────────
//
// Turns a natural-language GOAL into an ordered, typed plan of SAFE ops the
// client executor runs step-by-step: navigate · fillForm · search · findSimilar
// · analyze · suggestLinks · generateRecord · createRecord · linkRecords.
// Write steps carry `confirm: true` and MUST be confirm-gated in the UI.
// `{{stepN.field}}` placeholders in later args are resolved from prior step
// outputs by the executor. LLM-heavy → 120s timeout. Array normalized across
// the backend's differing keys (plan / steps / hits / items / results).

export interface AgentPlanFormField {
  name: string
  label?: string
  type?: string
  enum?: string[]
}

export interface AgentPlanForm {
  form_id: string
  entity_type: string
  fields: AgentPlanFormField[]
}

export interface AgentPlanRequest {
  goal: string
  module?: string
  available_forms?: AgentPlanForm[]
  max_steps?: number
}

export type AgentPlanOp =
  | 'navigate'
  | 'fillForm'
  | 'search'
  | 'findSimilar'
  | 'analyze'
  | 'suggestLinks'
  | 'generateRecord'
  | 'createRecord'
  | 'linkRecords'

export interface AgentPlanStep {
  step: number
  op: AgentPlanOp | string
  args: Record<string, unknown>
  side_effect?: boolean
  confirm?: boolean
  label?: string
}

export interface AgentPlanResult {
  assistant_message: string
  plan: AgentPlanStep[]
  requires_confirmation: boolean
  grounded: boolean
  model: string
}

/** Agent planning is a multi-tool LLM call → generous 2-min ceiling. */
const COPILOT_AGENT_TIMEOUT = 120000

Object.assign(copilotApi, {
  /** F8 — plan an ordered, confirm-gated sequence of safe ops for a GOAL. */
  agentPlan: async (req: AgentPlanRequest): Promise<AgentPlanResult> => {
    const d = await post<any, AgentPlanRequest>(
      '/ai/copilot/agent/plan',
      { max_steps: 8, ...req },
      { timeout: COPILOT_AGENT_TIMEOUT },
    )
    const rawPlan = (
      Array.isArray(d)
        ? d
        : (d?.plan ?? d?.steps ?? d?.hits ?? d?.suggestions ?? d?.items ?? d?.results ?? [])
    ) as any[]
    return {
      assistant_message: d?.assistant_message ?? d?.message ?? d?.summary ?? '',
      plan: (Array.isArray(rawPlan) ? rawPlan : []).map((s: any, i: number) => ({
        step: typeof s?.step === 'number' ? s.step : i + 1,
        op: String(s?.op ?? s?.operation ?? s?.action ?? s?.tool ?? 'analyze'),
        args: (s?.args ?? s?.arguments ?? s?.params ?? s?.input ?? {}) as Record<string, unknown>,
        side_effect: Boolean(s?.side_effect ?? s?.sideEffect),
        confirm: Boolean(s?.confirm ?? s?.requires_confirmation ?? s?.confirmation),
        label: s?.label ?? s?.description ?? s?.title ?? '',
      })),
      requires_confirmation: Boolean(d?.requires_confirmation ?? d?.requiresConfirmation),
      grounded: Boolean(d?.grounded),
      model: d?.model ?? '',
    }
  },
})

export interface CopilotApiAgent {
  agentPlan: (req: AgentPlanRequest) => Promise<AgentPlanResult>
}
/** Fully-typed view of copilotApi including the v2 + agent extensions. */
export const copilotApiAgent = copilotApi as typeof copilotApi & CopilotApiV2 & CopilotApiAgent

/** F8 — plan an agentic sequence of safe ops for a natural-language goal. */
export function useAgentPlan() {
  return useMutation({
    mutationFn: (req: AgentPlanRequest) => copilotApiAgent.agentPlan(req),
  })
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const copilotKeys = {
  all: ['copilot'] as const,
  chat: () => [...copilotKeys.all, 'chat'] as const,
  summary: (context: string) => [...copilotKeys.all, 'summary', context] as const,
  search: (query: string) => [...copilotKeys.all, 'search', query] as const,
  recommendations: (context: string) => [...copilotKeys.all, 'recommendations', context] as const,
  anomalies: (context: string) => [...copilotKeys.all, 'anomalies', context] as const,
  knowledgeSearch: (query: string, types?: string[]) =>
    [...copilotKeys.all, 'knowledge-search', query, (types ?? []).join(',')] as const,
  similar: (entityType: string, entityId: string) =>
    [...copilotKeys.all, 'similar', entityType, entityId] as const,
  inbox: (module: string, limit: number) =>
    [...copilotKeys.all, 'inbox', module, limit] as const,
}

// ── React Query Hooks ────────────────────────────────────────────────────────

export function useCopilotChat() {
  return useMutation({
    mutationFn: copilotApi.chat,
  })
}

export function useCopilotSummary(context: string, data: unknown, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.summary(context),
    queryFn: () => copilotApi.summarize({ context, data }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCopilotSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.search(query),
    queryFn: () => copilotApi.search(query),
    enabled: enabled && query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useDocumentAnalysis() {
  return useMutation({
    mutationFn: ({ file, moduleContext }: { file: File; moduleContext: string }) =>
      copilotApi.analyzeDocument(file, moduleContext),
  })
}

export function useWhatIfAnalysis() {
  return useMutation({
    mutationFn: ({
      scenario,
      moduleContext,
      currentData,
    }: {
      scenario: WhatIfScenario
      moduleContext: string
      currentData: unknown
    }) => copilotApi.whatIf(scenario, moduleContext, currentData),
  })
}

export function useCopilotRecommendations(moduleContext: string, tenantData?: unknown, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.recommendations(moduleContext),
    queryFn: () => copilotApi.getRecommendations(moduleContext, tenantData),
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useCopilotAnomalies(moduleContext: string, metrics?: unknown, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.anomalies(moduleContext),
    queryFn: () => copilotApi.getAnomalies(moduleContext, metrics),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// ── RAG hooks ────────────────────────────────────────────────────────────────

/** Debounced cross-entity semantic search (global command-bar / SmartSearch). */
export function useKnowledgeSearch(query: string, types?: string[], enabled = true) {
  return useQuery({
    queryKey: copilotKeys.knowledgeSearch(query, types),
    queryFn: () => copilotApi.searchEntities(query, types),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30 * 1000,
  })
}

/** "More like this" — vector-similar records for a given entity. */
export function useSimilarEntities(
  entityType: string,
  entityId: string,
  limit = 8,
  enabled = true,
) {
  return useQuery({
    queryKey: copilotKeys.similar(entityType, entityId),
    queryFn: () => copilotApi.similarEntities(entityType, entityId, limit),
    enabled: enabled && !!entityType && !!entityId,
    staleTime: 60 * 1000,
  })
}

/** Grounded RAG question-answering (RagAnalysisPanel). */
export function useKnowledgeAnalyze() {
  return useMutation({
    mutationFn: (request: KnowledgeAnalyzeRequest) => copilotApi.analyzeEntities(request),
  })
}

/** AI form-fill from a prompt and/or document text (AiFormFill). */
export function useCopilotFormFill() {
  return useMutation({
    mutationFn: (request: CopilotFormFillRequest) => copilotApi.fillForm(request),
  })
}

/** Upload → extract text (+ optional auto-fill) from a file (AiFormFill). */
export function useCopilotExtractFile() {
  return useMutation({
    mutationFn: ({
      file,
      formSchema,
      module,
    }: {
      file: File
      formSchema?: unknown
      module?: string
    }) => copilotApi.extractFile(file, formSchema, module),
  })
}

/** Contextual grounded advice for a module/entity. */
export function useCopilotAdvise() {
  return useMutation({
    mutationFn: (request: CopilotAdviceRequest) => copilotApi.advise(request),
  })
}

// ── Copilot v2 hooks ─────────────────────────────────────────────────────────

/** F9 — proactive "À traiter" inbox (dashboard / sidebar widget). */
export function useCopilotInbox(module = 'all', limit = 10, enabled = true) {
  return useQuery({
    queryKey: copilotKeys.inbox(module, limit),
    queryFn: () => copilotApiV2.getInbox(module, limit),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/** F3 — suggest existing records to link (risk→control, …). */
export function useSuggestLinks() {
  return useMutation({
    mutationFn: (req: SuggestLinksRequest) => copilotApiV2.suggestLinks(req),
  })
}

/** F1 — generate a full record draft from a prompt. */
export function useGenerateRecord() {
  return useMutation({
    mutationFn: (req: GenerateRecordRequest) => copilotApiV2.generateRecord(req),
  })
}

/** F4 — regulatory-change impact analysis + action plan. */
export function useRegImpact() {
  return useMutation({
    mutationFn: (req: RegImpactRequest) => copilotApiV2.regImpact(req),
  })
}

// ── Copilot v3 — LIVE endpoints ────────────────────────────────────────────────
//
// The Copilot 3.0 family, all on the SAME /ai/copilot router (→ api-gateway in
// cloud-direct). Every call is LLM-heavy and grounded-French → 120s timeout.
// Arrays are normalized across the backend's differing keys.

/** RPA bulk import — one file/text → MANY extracted record drafts. */
export interface ImportRecordDraft {
  fields: Record<string, unknown>
  confidence: number
  source_ref?: string
}
export interface ImportRecordsResult {
  records: ImportRecordDraft[]
  count: number
  notes: string
  unfilled_summary: string
}
export interface ImportRecordsRequest {
  /** Attach a file (csv/xlsx/pdf/txt) OR pass document_text. */
  file?: File
  document_text?: string
  entity_type: string
  module?: string
  form_schema?: unknown
}

export interface MeetingDecision {
  text: string
}
export interface MeetingActionItem {
  title: string
  owner?: string
  due_hint?: string
  priority?: string
}
export interface MeetingActionsResult {
  summary: string
  decisions: MeetingDecision[]
  action_items: MeetingActionItem[]
  risks_flagged: string[]
}
export interface MeetingActionsRequest {
  notes?: string
  document_text?: string
  module?: string
}

export interface CategorizeResult {
  category: string
  subcategory?: string
  tags: string[]
  confidence: number
  rationale: string
}
export interface CategorizeRequest {
  entity_type: string
  text: string
  taxonomy?: string[]
}

export interface FrameworkGapCovered {
  requirement: string
  evidence_entity_ref?: string
}
export interface FrameworkGapItem {
  requirement: string
  severity: string
  recommendation: string
}
export interface FrameworkGapResult {
  framework: string
  coverage_pct: number
  covered: FrameworkGapCovered[]
  gaps: FrameworkGapItem[]
  summary: string
  grounded: boolean
}
export interface FrameworkGapRequest {
  framework: string
  module?: string
}

export interface QuestionnaireSource {
  entity_type?: string
  entity_id?: string
  title?: string
}
export interface QuestionnaireAnswer {
  question: string
  answer: string
  confidence: number
  sources: QuestionnaireSource[]
  status: string
}
export interface AnswerQuestionnaireResult {
  answers: QuestionnaireAnswer[]
  answered_count: number
}
export interface AnswerQuestionnaireRequest {
  /** Upload a questionnaire file OR pass a list of questions. */
  file?: File
  questions?: string[]
}

/**
 * Coerce a "reference" field to a safe string. The backend sometimes returns a
 * ref as an object ({entity_type, entity_id, title}) rather than a string; this
 * flattens it so it can never be rendered directly as a React child (avoids
 * React error #31). Returns undefined for empty values.
 */
function refToString(v: unknown): string | undefined {
  if (v == null || v === '') return undefined
  if (typeof v === 'string') return v
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>
    const s = o.title ?? o.entity_id ?? o.id ?? o.ref ?? o.name
    if (s != null && s !== '') return String(s)
    try { return JSON.stringify(v) } catch { return undefined }
  }
  return String(v)
}

/**
 * Flatten a "summary" that may be a plain string OR a {field: count} object
 * (e.g. import-records `unfilled_summary: {owner_team: 3, sla: 3}`) into a
 * readable FR string so it can never be rendered as a React child (React #31).
 */
function summaryToString(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ')
  if (typeof v === 'object') {
    return Object.entries(v as Record<string, unknown>)
      .map(([k, n]) => (typeof n === 'number' ? `${k} (${n})` : `${k}`))
      .join(', ')
  }
  return String(v)
}

Object.assign(copilotApi, {
  /** V3 — extract MANY record drafts from a file or pasted text (cap 50). */
  importRecords: async (req: ImportRecordsRequest): Promise<ImportRecordsResult> => {
    let d: any
    if (req.file) {
      const fd = new FormData()
      fd.append('file', req.file)
      fd.append('entity_type', req.entity_type)
      if (req.module) fd.append('module', req.module)
      if (req.form_schema !== undefined) fd.append('form_schema', JSON.stringify(req.form_schema))
      d = await post<any>('/ai/copilot/import-records', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: COPILOT_LLM_TIMEOUT,
      })
    } else {
      d = await post<any>(
        '/ai/copilot/import-records',
        {
          document_text: req.document_text ?? '',
          entity_type: req.entity_type,
          ...(req.module ? { module: req.module } : {}),
          ...(req.form_schema !== undefined ? { form_schema: req.form_schema } : {}),
        },
        { timeout: COPILOT_LLM_TIMEOUT },
      )
    }
    const raw = Array.isArray(d) ? d : (d?.records ?? d?.items ?? d?.results ?? [])
    const records: ImportRecordDraft[] = (Array.isArray(raw) ? raw : []).map((r: any) => ({
      fields: (r?.fields ?? r?.values ?? r ?? {}) as Record<string, unknown>,
      confidence: typeof r?.confidence === 'number' ? r.confidence : 0,
      source_ref: refToString(r?.source_ref ?? r?.sourceRef),
    }))
    return {
      records,
      count: typeof d?.count === 'number' ? d.count : records.length,
      notes: summaryToString(d?.notes),
      unfilled_summary: summaryToString(d?.unfilled_summary ?? d?.unfilledSummary),
    }
  },

  /** V3 — turn meeting notes into summary + decisions + action items + risks. */
  meetingActions: async (req: MeetingActionsRequest): Promise<MeetingActionsResult> => {
    const d = await post<any>(
      '/ai/copilot/meeting-actions',
      {
        ...(req.notes ? { notes: req.notes } : {}),
        ...(req.document_text ? { document_text: req.document_text } : {}),
        ...(req.module ? { module: req.module } : {}),
      },
      { timeout: COPILOT_LLM_TIMEOUT },
    )
    const decisions: MeetingDecision[] = (Array.isArray(d?.decisions) ? d.decisions : []).map(
      (x: any) => ({ text: typeof x === 'string' ? x : (refToString(x?.text ?? x?.decision) ?? '') }),
    )
    const rawActions = Array.isArray(d?.action_items) ? d.action_items : (d?.actions ?? [])
    const action_items: MeetingActionItem[] = (Array.isArray(rawActions) ? rawActions : []).map(
      (x: any) => ({
        title: (typeof x === 'string' ? x : refToString(x?.title ?? x?.text)) ?? '',
        owner: refToString(x?.owner ?? x?.assignee),
        due_hint: refToString(x?.due_hint ?? x?.dueHint ?? x?.due),
        priority: refToString(x?.priority),
      }),
    )
    const rawRisks = Array.isArray(d?.risks_flagged) ? d.risks_flagged : (d?.risks ?? [])
    const risks_flagged: string[] = (Array.isArray(rawRisks) ? rawRisks : []).map((x: any) =>
      typeof x === 'string' ? x : (x?.text ?? x?.title ?? x?.risk ?? ''),
    )
    return {
      summary: summaryToString(d?.summary),
      decisions,
      action_items,
      risks_flagged,
    }
  },

  /** V3 — suggest a category / sub-category / tags for a free-text record. */
  categorize: async (req: CategorizeRequest): Promise<CategorizeResult> => {
    const d = await post<any>(
      '/ai/copilot/categorize',
      {
        entity_type: req.entity_type,
        text: req.text,
        ...(req.taxonomy && req.taxonomy.length ? { taxonomy: req.taxonomy } : {}),
      },
      { timeout: COPILOT_LLM_TIMEOUT },
    )
    return {
      category: refToString(d?.category) ?? '',
      subcategory: refToString(d?.subcategory ?? d?.sub_category),
      tags: Array.isArray(d?.tags) ? d.tags.map((t: any) => refToString(t) ?? '').filter(Boolean) : [],
      confidence: typeof d?.confidence === 'number' ? d.confidence : 0,
      rationale: refToString(d?.rationale) ?? '',
    }
  },

  /** V3 — regulatory framework coverage-gap analysis grounded on tenant data. */
  frameworkGap: async (req: FrameworkGapRequest): Promise<FrameworkGapResult> => {
    const d = await post<any>(
      '/ai/copilot/framework-gap',
      {
        framework: req.framework,
        ...(req.module ? { module: req.module } : {}),
      },
      { timeout: COPILOT_LLM_TIMEOUT },
    )
    const covered: FrameworkGapCovered[] = (Array.isArray(d?.covered) ? d.covered : []).map(
      (x: any) => ({
        requirement: (typeof x === 'string' ? x : refToString(x?.requirement ?? x?.text)) ?? '',
        evidence_entity_ref: refToString(x?.evidence_entity_ref ?? x?.evidenceEntityRef ?? x?.evidence),
      }),
    )
    const gaps: FrameworkGapItem[] = (Array.isArray(d?.gaps) ? d.gaps : []).map((x: any) => ({
      requirement: (typeof x === 'string' ? x : refToString(x?.requirement ?? x?.text)) ?? '',
      severity: String(x?.severity ?? 'medium'),
      recommendation: refToString(x?.recommendation ?? x?.reco) ?? '',
    }))
    return {
      framework: d?.framework ?? req.framework,
      coverage_pct: typeof d?.coverage_pct === 'number' ? d.coverage_pct : Number(d?.coverage_pct ?? 0) || 0,
      covered,
      gaps,
      summary: summaryToString(d?.summary),
      grounded: Boolean(d?.grounded),
    }
  },

  /** V3 — auto-answer a security/compliance questionnaire from tenant evidence. */
  answerQuestionnaire: async (
    req: AnswerQuestionnaireRequest,
  ): Promise<AnswerQuestionnaireResult> => {
    let d: any
    if (req.file) {
      const fd = new FormData()
      fd.append('file', req.file)
      d = await post<any>('/ai/copilot/answer-questionnaire', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: COPILOT_LLM_TIMEOUT,
      })
    } else {
      d = await post<any>(
        '/ai/copilot/answer-questionnaire',
        { questions: req.questions ?? [] },
        { timeout: COPILOT_LLM_TIMEOUT },
      )
    }
    const raw = Array.isArray(d) ? d : (d?.answers ?? d?.items ?? d?.results ?? [])
    const answers: QuestionnaireAnswer[] = (Array.isArray(raw) ? raw : []).map((a: any) => ({
      question: refToString(a?.question ?? a?.q) ?? '',
      answer: refToString(a?.answer ?? a?.a) ?? '',
      confidence: typeof a?.confidence === 'number' ? a.confidence : 0,
      sources: (Array.isArray(a?.sources) ? a.sources : []).map((s: any) => {
        if (typeof s === 'string') return { title: s }
        return {
          entity_type: refToString(s?.entity_type ?? s?.entityType),
          entity_id: refToString(s?.entity_id ?? s?.entityId),
          title: refToString(s?.title ?? s?.name),
        }
      }),
      status: a?.status ?? 'needs_review',
    }))
    return {
      answers,
      answered_count:
        typeof d?.answered_count === 'number'
          ? d.answered_count
          : answers.filter((x) => x.status === 'answered').length,
    }
  },
})

export interface CopilotApiV3 {
  importRecords: (req: ImportRecordsRequest) => Promise<ImportRecordsResult>
  meetingActions: (req: MeetingActionsRequest) => Promise<MeetingActionsResult>
  categorize: (req: CategorizeRequest) => Promise<CategorizeResult>
  frameworkGap: (req: FrameworkGapRequest) => Promise<FrameworkGapResult>
  answerQuestionnaire: (req: AnswerQuestionnaireRequest) => Promise<AnswerQuestionnaireResult>
}
/** Fully-typed view of copilotApi including the v2 + agent + v3 extensions. */
export const copilotApiV3 = copilotApi as typeof copilotApi &
  CopilotApiV2 &
  CopilotApiAgent &
  CopilotApiV3

// ── Copilot v3 hooks ───────────────────────────────────────────────────────────

/** RPA bulk import — file/text → many record drafts (ImportRecords). */
export function useImportRecords() {
  return useMutation({
    mutationFn: (req: ImportRecordsRequest) => copilotApiV3.importRecords(req),
  })
}

/** Meeting notes → summary/decisions/actions/risks (MeetingActions). */
export function useMeetingActions() {
  return useMutation({
    mutationFn: (req: MeetingActionsRequest) => copilotApiV3.meetingActions(req),
  })
}

/** Suggest category + tags for a record (Categorize). */
export function useCategorize() {
  return useMutation({
    mutationFn: (req: CategorizeRequest) => copilotApiV3.categorize(req),
  })
}

/** Framework coverage-gap analysis (FrameworkGap). */
export function useFrameworkGap() {
  return useMutation({
    mutationFn: (req: FrameworkGapRequest) => copilotApiV3.frameworkGap(req),
  })
}

/** Auto-answer a questionnaire (AnswerQuestionnaire). */
export function useAnswerQuestionnaire() {
  return useMutation({
    mutationFn: (req: AnswerQuestionnaireRequest) => copilotApiV3.answerQuestionnaire(req),
  })
}
